import { Box, Container, Flex, Heading, Image, Text, VStack, HStack, OrderedList, UnorderedList, ListItem, Link as ChakraLink, Divider } from '@chakra-ui/react';
import { Link } from 'react-router-dom';

export default function DisputeResolutionPolicy() {
  return (
    <Box bg="#faf9f7" minH="100vh">
      <Box bg="gray.900" py={0}>
        <Container maxW="6xl">
          <Flex align="center" justify="space-between" h="76px">
            <ChakraLink as={Link} to="/">
              <Image src="/attributed-to-logo-sm.webp" alt="attributed.to" h="60px" />
            </ChakraLink>
            <ChakraLink as={Link} to="/" color="whiteAlpha.700" fontSize="sm" fontWeight="400" _hover={{ color: 'white' }}>
              &larr; Back to home
            </ChakraLink>
          </Flex>
        </Container>
      </Box>
      <Container maxW="3xl" py={{ base: 12, md: 20 }} pb={{ base: 20, md: 28 }}>
        <VStack align="stretch" spacing={10}>
          <Box mb={2}>
            <Text
              fontSize="sm"
              fontWeight="600"
              letterSpacing="0.15em"
              textTransform="uppercase"
              color="gray.400"
              mb={4}
            >
              Legal
            </Text>
            <Heading
              fontSize={{ base: '3xl', md: '4xl' }}
              fontWeight="300"
              color="gray.900"
              letterSpacing="-0.02em"
              mb={3}
            >
              Dispute Resolution Policy
            </Heading>
            <Text fontSize="sm" color="gray.400" fontWeight="400">Effective Date: March 07, 2026</Text>
          </Box>

          <Section title="">
            <Body>
              This Dispute Resolution Policy explains how disputes are handled on attributed.to, a marketplace where users post research tasks and researchers submit claims and answers in response to those tasks, including tasks involving art historical research.
            </Body>
            <Body>
              By using attributed.to, both users and researchers agree to this policy.
            </Body>
          </Section>

          <Section title="1. Purpose">
            <Body>
              The purpose of this policy is to provide a fair, consistent, and transparent process for resolving disputes about research submissions, payments, and refunds.
            </Body>
            <Body>
              Because art historical research can involve interpretation, judgment, and competing scholarly views, disputes are not decided solely on whether every reviewer would reach the same conclusion. Instead, attributed.to evaluates whether the researcher's submission is reasonably responsive to the task and supported by credible evidence, methodology, and research practice.
            </Body>
          </Section>

          <Section title="2. Scope">
            <Body>
              This policy applies to disputes including, but not limited to, claims that a research submission:
            </Body>
            <UnorderedList spacing={2} pl={4} color="gray.500" fontSize="sm" fontWeight="300">
              <ListItem>does not answer the posted task;</ListItem>
              <ListItem>is materially incomplete, inaccurate, unsupported, misleading, or irrelevant;</ListItem>
              <ListItem>fails to meet the task requirements or stated deliverables;</ListItem>
              <ListItem>relies on inadequate, unreliable, or improperly cited sources;</ListItem>
              <ListItem>omits important uncertainty, limitations, or competing interpretations; or</ListItem>
              <ListItem>otherwise fails to satisfy the standards of the marketplace.</ListItem>
            </UnorderedList>
            <Body>
              This policy also applies where a researcher contends that a submission met the task requirements and should be paid.
            </Body>
          </Section>

          <Section title="3. Final Decision Authority">
            <Body>
              All disputes are reviewed and decided by attributed.to in its sole discretion.
            </Body>
            <Body>
              After reviewing the available materials, attributed.to will decide whether:
            </Body>
            <OrderedList spacing={2} pl={4} color="gray.500" fontSize="sm" fontWeight="300">
              <ListItem>the researcher should be paid; or</ListItem>
              <ListItem>the user should be refunded.</ListItem>
            </OrderedList>
            <Body>
              The decision of attributed.to is final except where otherwise required by applicable law.
            </Body>
          </Section>

          <Section title="4. Payment Responsibility During a Dispute">
            <Body>
              Opening a dispute does <strong>not</strong> suspend the user's payment obligation.
            </Body>
            <Body>
              Until attributed.to issues a final decision, the user remains responsible for payment of the <strong>full amount</strong> associated with the task, including any applicable fees under the platform's Terms. attributed.to may authorize, collect, reserve, or hold the full amount while the dispute is under review.
            </Body>
            <Body>
              If attributed.to determines that the researcher satisfied the task requirements, the researcher will be paid in accordance with the platform's Terms.
            </Body>
            <Body>
              If attributed.to determines that the researcher did not satisfy the task requirements, the user will be refunded in accordance with the platform's Terms and payment policies.
            </Body>
          </Section>

          <Section title="5. How to Start a Dispute">
            <Body>
              A user or researcher may submit a dispute by clicking the Create Dispute button on a pending research claim web application within <strong>one week</strong> after the submission.
            </Body>
            <Body>
              A user creating a dispute is encouraged to provide, where applicable:
            </Body>
            <UnorderedList spacing={2} pl={4} color="gray.500" fontSize="sm" fontWeight="300">
              <ListItem>a clear explanation of the issue;</ListItem>
              <ListItem>the specific part of the submission or conduct being challenged;</ListItem>
              <ListItem>supporting documents, screenshots, citations, images, correspondence, or links; and</ListItem>
              <ListItem>any other information reasonably requested by attributed.to.</ListItem>
            </UnorderedList>
            <Body>
              Incomplete or unsupported dispute submissions may be denied or resolved based on the information available.
            </Body>
          </Section>

          <Section title="6. Review Process">
            <Body>
              When a dispute is opened, attributed.to may, in its discretion:
            </Body>
            <UnorderedList spacing={2} pl={4} color="gray.500" fontSize="sm" fontWeight="300">
              <ListItem>review the original task posting and any instructions;</ListItem>
              <ListItem>review the researcher's submission, sources, attachments, and cited materials;</ListItem>
              <ListItem>review messages and communications between the parties on the platform;</ListItem>
              <ListItem>request clarifications, corrections, source details, or additional evidence from either party;</ListItem>
              <ListItem>compare the submission against the task requirements and platform standards;</ListItem>
              <ListItem>consult internal reviewers, moderators, or admins; and</ListItem>
              <ListItem>consult third-party experts where appropriate.</ListItem>
            </UnorderedList>
            <Body>
              Each party is expected to cooperate promptly and honestly. Failure to respond, refusal to provide requested materials, or submission of false or misleading information may weigh against that party.
            </Body>
          </Section>

          <Section title="7. Standard of Review for Art Historical Research">
            <Body>
              Because art historical research often involves attribution questions, provenance analysis, stylistic comparison, archival interpretation, language analysis, and competing scholarly views, attributed.to does not require absolute certainty in order for a submission to be valid.
            </Body>
            <Body>
              Instead, attributed.to generally considers whether the researcher's submission was:
            </Body>
            <UnorderedList spacing={2} pl={4} color="gray.500" fontSize="sm" fontWeight="300">
              <ListItem>responsive to the task as posted;</ListItem>
              <ListItem>grounded in identifiable and relevant evidence;</ListItem>
              <ListItem>supported by a reasonable research method;</ListItem>
              <ListItem>transparent about uncertainty and limitations;</ListItem>
              <ListItem>consistent with the weight of available evidence, or at minimum reasonably defended where the evidence is mixed; and</ListItem>
              <ListItem>presented in good faith without fabrication, plagiarism, or material omission.</ListItem>
            </UnorderedList>
            <Body>
              A submission may still be acceptable even if reasonable experts could disagree, so long as the submission is serious, evidence-based, and materially complies with the task.
            </Body>
          </Section>

          <Section title="8. Best Practices We Use to Verify Disputed Research">
            <Body>
              In reviewing disputed research, attributed.to may apply one or more of the following best practices, depending on the nature of the task:
            </Body>
            <SubSection title="A. Source Verification">
              <Body>
                We review whether the submission relies on sources that are relevant, traceable, and appropriate to the question, such as:
              </Body>
              <UnorderedList spacing={2} pl={4} color="gray.500" fontSize="sm" fontWeight="300">
                <ListItem>museum records and collection databases;</ListItem>
                <ListItem>catalogues raisonn&eacute;s;</ListItem>
                <ListItem>auction catalogues and sales records;</ListItem>
                <ListItem>exhibition catalogues;</ListItem>
                <ListItem>archival documents and primary-source records;</ListItem>
                <ListItem>academic books, journals, and scholarly publications;</ListItem>
                <ListItem>conservation reports, technical studies, or provenance materials; and</ListItem>
                <ListItem>reputable image repositories or institutional records.</ListItem>
              </UnorderedList>
              <Body>
                We may give greater weight to primary and authoritative sources than to unsourced assertions, anonymous commentary, or secondary summaries.
              </Body>
            </SubSection>
            <SubSection title="B. Citation and Traceability Review">
              <Body>
                We review whether the researcher has made it possible to trace key conclusions back to underlying evidence. This may include checking whether citations are sufficiently specific, whether quoted material is accurate, and whether sources actually support the proposition for which they are cited.
              </Body>
            </SubSection>
            <SubSection title="C. Methodology Review">
              <Body>
                We examine whether the researcher used a reasonable method for the specific task. In art historical matters, this can include review of:
              </Body>
              <UnorderedList spacing={2} pl={4} color="gray.500" fontSize="sm" fontWeight="300">
                <ListItem>attribution logic;</ListItem>
                <ListItem>stylistic or comparative analysis;</ListItem>
                <ListItem>provenance reconstruction;</ListItem>
                <ListItem>inscription, signature, or mark analysis;</ListItem>
                <ListItem>chronology and geographic consistency;</ListItem>
                <ListItem>object history and documentary support;</ListItem>
                <ListItem>translation or interpretation of foreign-language sources; and</ListItem>
                <ListItem>whether alternative explanations were considered.</ListItem>
              </UnorderedList>
            </SubSection>
            <SubSection title="D. Consistency and Plausibility Checks">
              <Body>
                We compare the submission against known facts and established reference points, including dates, materials, artist biographies, workshop practices, provenance timelines, and institutional records. Internal inconsistencies, unsupported leaps in reasoning, or factual contradictions may weigh against the submission.
              </Body>
            </SubSection>
            <SubSection title="E. Originality and Integrity Review">
              <Body>
                We may assess whether the submission appears original and honestly prepared. This can include checking for plagiarism, unattributed copying, fabricated citations, altered evidence, or other conduct inconsistent with scholarly integrity.
              </Body>
            </SubSection>
            <SubSection title="F. Disclosure of Uncertainty">
              <Body>
                In fields like art history, certainty is not always possible. We consider whether the researcher appropriately disclosed uncertainty, conflicting evidence, or the limits of the available record. Overstated certainty without support may weigh against the submission.
              </Body>
            </SubSection>
            <SubSection title="G. Follow-Up Clarification">
              <Body>
                Where needed, we may ask the researcher to explain methodology, identify additional sources, clarify ambiguous statements, or respond to specific objections raised by the user.
              </Body>
            </SubSection>
          </Section>

          <Section title="9. Use of Third-Party Experts">
            <Body>
              For complex, specialized, or high-value disputes, attributed.to may consult one or more independent third-party experts.
            </Body>
            <Body>
              These experts may include, depending on the issue:
            </Body>
            <UnorderedList spacing={2} pl={4} color="gray.500" fontSize="sm" fontWeight="300">
              <ListItem>art historians;</ListItem>
              <ListItem>curators;</ListItem>
              <ListItem>provenance researchers;</ListItem>
              <ListItem>cataloguers;</ListItem>
              <ListItem>archivists;</ListItem>
              <ListItem>conservators or technical specialists;</ListItem>
              <ListItem>regional or period specialists; or</ListItem>
              <ListItem>language or epigraphy specialists.</ListItem>
            </UnorderedList>
            <Body>
              When using third-party experts, attributed.to seeks to follow these practices:
            </Body>
            <UnorderedList spacing={2} pl={4} color="gray.500" fontSize="sm" fontWeight="300">
              <ListItem>selecting experts with relevant subject-matter expertise;</ListItem>
              <ListItem>avoiding known conflicts of interest where reasonably possible;</ListItem>
              <ListItem>sharing only the materials reasonably necessary for review;</ListItem>
              <ListItem>requesting an impartial, evidence-based opinion;</ListItem>
              <ListItem>considering the expert's methodology and reasoning, not just the conclusion; and</ListItem>
              <ListItem>treating the expert opinion as an important input, while reserving final decision-making authority to attributed.to.</ListItem>
            </UnorderedList>
            <Body>
              Third-party experts may disagree with one another, especially in matters of attribution or interpretation. attributed.to is not required to follow any single expert opinion and may weigh multiple opinions alongside the rest of the evidence.
            </Body>
          </Section>

          <Section title="10. Confidentiality and Data Sharing">
            <Body>
              To investigate a dispute, attributed.to may review and share relevant task materials, submissions, communications, and supporting evidence with internal reviewers or third-party experts on a need-to-know basis.
            </Body>
            <Body>
              Where appropriate, attributed.to may redact or limit personal information before sharing materials for review. By using the platform, users and researchers authorize attributed.to to use submitted materials for dispute investigation and resolution consistent with the platform's Terms and Privacy Policy.
            </Body>
          </Section>

          <Section title="11. Bad-Faith Conduct">
            <Body>
              attributed.to may deny a dispute, suspend an account, reverse platform privileges, or take other action under its Terms where a party engages in bad-faith conduct, including:
            </Body>
            <UnorderedList spacing={2} pl={4} color="gray.500" fontSize="sm" fontWeight="300">
              <ListItem>knowingly submitting false or misleading evidence;</ListItem>
              <ListItem>fabricating citations or source materials;</ListItem>
              <ListItem>plagiarism or misappropriation;</ListItem>
              <ListItem>harassment or abusive conduct during the dispute process;</ListItem>
              <ListItem>attempting to manipulate reviewers or experts; or</ListItem>
              <ListItem>using the dispute process to avoid legitimate payment.</ListItem>
            </UnorderedList>
          </Section>

          <Section title="12. No Guarantee of Scholarly Consensus">
            <Body>
              Users and researchers acknowledge that art historical research may involve unresolved questions, incomplete archives, evolving scholarship, and legitimate differences of interpretation.
            </Body>
            <Body>
              Accordingly, attributed.to does not guarantee unanimous agreement, definitive attribution, or universal scholarly acceptance. Disputes are resolved based on the quality, credibility, and relevance of the evidence available at the time of review.
            </Body>
          </Section>

          <Section title="13. Relationship to Other Terms">
            <Body>
              This Dispute Resolution Policy should be read together with the platform's Terms of Service, Payment Terms, Refund Policy, and Privacy Policy. If there is a conflict between this policy and the Terms of Service, the Terms of Service will control unless this policy expressly states otherwise.
            </Body>
          </Section>

          <Section title="14. Changes to This Policy">
            <Body>
              attributed.to may update this Dispute Resolution Policy from time to time by posting a revised version on the website. The revised version becomes effective as of the stated effective date.
            </Body>
          </Section>
        </VStack>
      </Container>

      <Box bg="gray.900" py={6}>
        <Container maxW="6xl">
          <HStack spacing={5}>
            <Text color="gray.500" fontSize="xs">&copy; {new Date().getFullYear()} attributed.to</Text>
            <ChakraLink as={Link} to="/terms" color="gray.600" fontSize="xs" _hover={{ color: 'gray.300' }}>Terms of Service</ChakraLink>
            <ChakraLink as={Link} to="/privacy" color="gray.600" fontSize="xs" _hover={{ color: 'gray.300' }}>Privacy Policy</ChakraLink>
          </HStack>
        </Container>
      </Box>
    </Box>
  );
}

function Section({ title, children }) {
  return (
    <Box>
      <Divider borderColor="gray.200" mb={10} />
      {title && (
        <Heading
          fontSize={{ base: 'md', md: 'lg' }}
          fontWeight="400"
          color="gray.800"
          letterSpacing="-0.01em"
          mb={4}
        >
          {title}
        </Heading>
      )}
      {children}
    </Box>
  );
}

function SubSection({ title, children }) {
  return (
    <Box mt={5}>
      <Heading fontSize="sm" fontWeight="600" color="gray.700" textTransform="uppercase" letterSpacing="0.05em" mb={3}>
        {title}
      </Heading>
      {children}
    </Box>
  );
}

function Body({ children }) {
  return (
    <Text color="gray.500" lineHeight="1.8" fontSize="sm" fontWeight="300" mb={3}>
      {children}
    </Text>
  );
}
